import React from 'react';

import { styled } from '@mui/material/styles';
import {
    Breadcrumbs,
    Link,
    Tooltip,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';

import { useBreakpoint } from 'common/hooks';

const getMaxItems = ({ breakpoint, locations } = {}) => {
    if (isEmpty(locations)) return 1;
    if (breakpoint === 'xs') return 2;
    if (breakpoint === 'sm') return 3;
    return locations.length + 1;
};

const StyledBreadCrumbs = styled(Breadcrumbs)(({ theme }) => ({
    '& .MuiBreadcrumbs-separator': { display: 'none' },
    '& .MuiBreadcrumbs-ol': {
        display: 'flex',
        padding: '0 0 0 16px',
        gap: '8px',
    },
    '& .MuiBreadcrumbs-li': {
        alignItems: 'center',
        background:
            theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.16)'
                : '#EEF4FA',
        border: 'none',
        borderBottomLeftRadius: '8px',
        borderTopLeftRadius: '8px',
        clipPath:
            'polygon(50% 0%, calc(100% - 15px) 0%, 100% 50%, calc(100% - 15px) 100%, 50% 100%, 0% 100%, 15px 50%, 0% 0%)',
        color: theme.palette.mode === 'dark' ? 'unset' : 'rgba(0, 0, 0, 0.6)',
        cursor: 'default',
        fontSize: '14px',
        fontWeight: '600',
        gap: '4px',
        lineHeight: '1.12rem',
        marginLeft: '-16px',
        maxWidth: 'fit-content',
        overflow: 'hidden',
        padding: '4px 20px',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    '& .MuiBreadcrumbs-li:first-of-type': {
        clipPath:
            'polygon(50% 0%, calc(100% - 15px) 0%, 100% 50%, calc(100% - 15px) 100%, 50% 100%, 0% 100%, 0% 0%)',
        padding: '4px 20px 4px 12px',
    },
    '& .MuiBreadcrumbs-li:last-child': {
        borderBottomRightRadius: '8px',
        borderTopRightRadius: '8px',
        clipPath:
            'polygon(50% 0%, 100% 0%, 100% 100%, 50% 100%, 0% 100%, 15px 50%, 0% 0%)',
        padding: '4px 12px 4px 20px',
    },
    ' & .MuiButtonBase-root': {
        marginRight: '16px',
        padding: 0,
    },
}));

const CollapsedBreadcrumbs = ({ current, locations, hasCollapse = true }) => {
    const breakpoint = useBreakpoint();
    return (
        <StyledBreadCrumbs
            aria-label="breadcrumb"
            {...(hasCollapse && {
                maxItems: getMaxItems({ breakpoint, locations }),
            })}
        >
            {locations.map((location, index) => {
                return (
                    <Tooltip
                        title={location.label ? location.label : ''}
                        key={`breadcrumb-${index}`}
                    >
                        <Link
                            color="inherit"
                            href={location.link ? location.link : '#'}
                            onClick={location.link ? () => {} : location.action}
                            sx={{
                                fontSize: '14px',
                                '&:hover': { color: 'primary.main' },
                            }}
                            underline="hover"
                        >
                            {location.label}
                        </Link>
                    </Tooltip>
                );
            })}
            <Typography sx={{ color: 'primary.main', fontSize: '14px' }}>
                {current}
            </Typography>
        </StyledBreadCrumbs>
    );
};

CollapsedBreadcrumbs.propTypes = {
    current: PropTypes.string,
    hasCollapse: PropTypes.bool,
    locations: PropTypes.array,
};

export default CollapsedBreadcrumbs;
